/**
 * Product Recommend Handler (V2)
 * - Orchestration only (no direct DOM mutation except via render)
 */

import { notifier } from "../../../core/notifier/notifier";
import { phpMaxRecommendRank } from "../../../core/php-data-access";
import { translator } from "../../../translation/translator";
import { z } from "zod";
import {
    GetProductRecommendMeta200Response,
    GetProductRecommendMeta401Response,
    GetProductRecommendMeta403Response,
    GetProductRecommendMeta404Response,
    GetProductRecommendMeta500Response,
    RemoveProductRecommend200Response,
    RemoveProductRecommend400Response,
    RemoveProductRecommend401Response,
    RemoveProductRecommend403Response,
    RemoveProductRecommend404Response,
    RemoveProductRecommend500Response,
    UpsertProductRecommend200Response,
    UpsertProductRecommend400Response,
    UpsertProductRecommend401Response,
    UpsertProductRecommend403Response,
    UpsertProductRecommend404Response,
    UpsertProductRecommend409Response,
    UpsertProductRecommend422Response,
    UpsertProductRecommend500Response,
} from "../../../api/generated/products.zod";
import { toPositiveIntOrNull } from "../../../util/id-utils";
import { productRecommendForm } from "./product-recommend-form";

type RecommendMetaResponse =
    | z.infer<typeof GetProductRecommendMeta200Response>
    | z.infer<typeof GetProductRecommendMeta401Response>
    | z.infer<typeof GetProductRecommendMeta403Response>
    | z.infer<typeof GetProductRecommendMeta404Response>
    | z.infer<typeof GetProductRecommendMeta500Response>;
type RecommendMutationResponse =
    | z.infer<typeof UpsertProductRecommend200Response>
    | z.infer<typeof UpsertProductRecommend400Response>
    | z.infer<typeof UpsertProductRecommend401Response>
    | z.infer<typeof UpsertProductRecommend403Response>
    | z.infer<typeof UpsertProductRecommend404Response>
    | z.infer<typeof UpsertProductRecommend409Response>
    | z.infer<typeof UpsertProductRecommend422Response>
    | z.infer<typeof UpsertProductRecommend500Response>
    | z.infer<typeof RemoveProductRecommend200Response>
    | z.infer<typeof RemoveProductRecommend400Response>
    | z.infer<typeof RemoveProductRecommend401Response>
    | z.infer<typeof RemoveProductRecommend403Response>
    | z.infer<typeof RemoveProductRecommend404Response>
    | z.infer<typeof RemoveProductRecommend500Response>;
type ClearAllRecommendMutationResponse = {
    success?: boolean;
    message?: string;
};

type RecommendButtonVm = {
    rank: number;
    disabled: boolean;
    isCurrent: boolean;
};

type ProductRecommendApiLike = {
    getMeta(productId: number): Promise<Response>;
    upsert(productId: number, rank: number | null): Promise<Response>;
    remove(productId: number): Promise<Response>;
    clearAll(): Promise<Response>;
};

type ProductRecommendRenderLike = {
    setActions(actions: {
        onPickRank?: ((rank: number) => Promise<void> | void) | null;
        onUnset?: (() => Promise<void> | void) | null;
        onClearAll?: (() => Promise<void> | void) | null;
    }): void;
    renderBody(vm: { buttons: RecommendButtonVm[]; canUnset: boolean }): void;
    renderLoading(): void;
    open(): void;
    close(): void;
    setBusy(busy: boolean): void;
    setCrownState(el: HTMLElement, recommended: boolean): void;
    setCrownRank(el: HTMLElement, rank: number | null): void;
    setCrownLoading(el: HTMLElement, loading: boolean): void;
    getOverlay(): HTMLElement | null;
};

export class ProductRecommendHandler {
    public api: ProductRecommendApiLike;

    public render: ProductRecommendRenderLike;

    public activeProductId: number | null;

    public activeCrownEl: HTMLElement | null;

    public busy: boolean;

    public constructor(deps: { api: ProductRecommendApiLike; render: ProductRecommendRenderLike }) {
        this.api = deps.api;
        this.render = deps.render;
        this.activeProductId = null;
        this.activeCrownEl = null;
        this.busy = false;
    }

    public getTranslatedMessage(key: string, fallback: string): string {
        const value = translator?.getTranslation(key);
        return value && value !== key ? value : fallback;
    }

    public isSuccessResponse(value: unknown): value is 1 {
        return value === 1;
    }

    public getResponseMessage(value: unknown): string {
        if (!value || typeof value !== "object") return "";
        const message = "message" in value ? value.message : undefined;
        return typeof message === "string" ? message : "";
    }

    public isRecommendMetaResponse(value: unknown): value is RecommendMetaResponse {
        return !!value && typeof value === "object" && "success" in value;
    }

    public isRecommendMetaSuccessResponse(
        value: RecommendMetaResponse,
    ): value is z.infer<typeof GetProductRecommendMeta200Response> {
        return this.isSuccessResponse(value.success) && "data" in value && !!value.data;
    }

    public isRecommendMutationResponse(value: unknown): value is RecommendMutationResponse {
        return !!value && typeof value === "object" && "success" in value;
    }

    public isRecommendMutationSuccessResponse(
        value: RecommendMutationResponse,
    ): value is z.infer<typeof UpsertProductRecommend200Response> | z.infer<typeof RemoveProductRecommend200Response> {
        return this.isSuccessResponse(value.success);
    }

    public async handleRecommendClick(event: MouseEvent, crownEl: HTMLElement): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const productId = toPositiveIntOrNull(crownEl.dataset.productId);
        if (productId === null) return;

        const card = productRecommendForm.getProductCard(crownEl);
        const canRecommend =
            crownEl.dataset?.canRecommend === "1" ||
            card?.dataset?.canRecommend === "1" ||
            !!productRecommendForm.getRecommendCapabilityHost(crownEl);
        if (!canRecommend) return;

        if (this.busy || crownEl.dataset.loading === "1") return;

        this.activeProductId = productId;
        this.activeCrownEl = crownEl;

        this.render.setActions({
            onPickRank: async (rank) => await this.setRank(rank),
            onUnset: async () => await this.unsetRecommend(),
            onClearAll: async () => await this.clearAllRecommends(),
        });

        this.render.renderLoading();
        this.render.open();

        try {
            const meta = await this.fetchMeta(productId);
            if (!meta) {
                this.render.close();
                return;
            }

            this.render.renderBody(meta);
        } catch (e) {
            console.error(e);
            notifier.error(this.getTranslatedMessage("product_recommend_error_load", "Failed to load recommend info"));
            this.render.close();
        }
    }

    public findAllCrowns(productId: number): HTMLElement[] {
        return productRecommendForm.getRecommendButtons(document, productId);
    }

    public findAllCrownsAll(): HTMLElement[] {
        return productRecommendForm.getRecommendButtons(document);
    }

    public async reloadMeta(): Promise<void> {
        if (!this.activeProductId) return;

        this.render.renderLoading();

        try {
            const meta = await this.fetchMeta(this.activeProductId);
            if (!meta) {
                this.render.close();
                return;
            }

            this.render.renderBody(meta);
        } catch (e) {
            console.error(e);
            notifier.error(this.getTranslatedMessage("product_recommend_error_load", "Failed to load recommend info"));
            this.render.close();
        }
    }

    public async handleModalClick(event: MouseEvent): Promise<void> {
        const overlay = this.render.getOverlay();
        if (!overlay) return;

        const target = event.target instanceof HTMLElement ? event.target : null;
        if (!target) return;

        if (!productRecommendForm.getOverlayHost(target)) return;

        event.stopPropagation();

        if (target === overlay) {
            if (this.busy) return;
            this.render.close();
            return;
        }

        const closeBtn = productRecommendForm.getCloseButton(target);
        const closeAction = productRecommendForm.getCloseAction(target);
        if (closeBtn || closeAction) {
            event.preventDefault();
            if (this.busy) return;
            this.render.close();
            return;
        }

        const unsetBtn = productRecommendForm.getUnsetAction(target);
        if (unsetBtn instanceof HTMLButtonElement) {
            event.preventDefault();
            if (unsetBtn.disabled || this.busy) return;
            await this.unsetRecommend();
            return;
        }

        const rankBtn = productRecommendForm.getRankAction(target);
        if (rankBtn instanceof HTMLButtonElement) {
            event.preventDefault();
            if (rankBtn.disabled || this.busy) return;
            const rank = Number(rankBtn.getAttribute("data-rank") || "0");
            if (!Number.isFinite(rank) || rank <= 0) return;
            await this.setRank(rank);
        }
    }

    public async setRank(rank: number): Promise<void> {
        if (!this.activeProductId || !this.activeCrownEl) return;

        this.render.renderLoading();

        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.upsert(this.activeProductId, rank);
            const json = (await res.json().catch(() => ({}))) as unknown;
            if (!res.ok || !this.isRecommendMutationResponse(json) || !this.isRecommendMutationSuccessResponse(json)) {
                notifier.error(
                    this.getResponseMessage(json) ||
                        this.getTranslatedMessage("product_recommend_error_update", "Failed to update recommend rank"),
                );
                await this.reloadMeta();
                return;
            }

            const crowns = this.findAllCrowns(this.activeProductId);
            crowns.forEach((el) => {
                this.render.setCrownState(el, true);
                this.render.setCrownRank(el, rank);
            });

            this.render.close();
        } catch (e) {
            console.error(e);
            notifier.error(
                this.getTranslatedMessage("product_recommend_error_update", "Failed to update recommend rank"),
            );
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    public async unsetRecommend(): Promise<void> {
        if (!this.activeProductId || !this.activeCrownEl) return;

        this.render.renderLoading();

        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.remove(this.activeProductId);
            const json = (await res.json().catch(() => ({}))) as unknown;
            if (!res.ok || !this.isRecommendMutationResponse(json) || !this.isRecommendMutationSuccessResponse(json)) {
                notifier.error(
                    this.getResponseMessage(json) ||
                        this.getTranslatedMessage("product_recommend_error_unset", "Failed to unset recommend"),
                );
                await this.reloadMeta();
                return;
            }

            const crowns = this.findAllCrowns(this.activeProductId);
            crowns.forEach((el) => {
                this.render.setCrownState(el, false);
                this.render.setCrownRank(el, null);
            });

            this.render.close();
        } catch (e) {
            console.error(e);
            notifier.error(this.getTranslatedMessage("product_recommend_error_unset", "Failed to unset recommend"));
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    public async clearAllRecommends(): Promise<void> {
        if (!this.activeProductId) return;

        const confirmMessage = this.getTranslatedMessage(
            "product_recommend_remove_all_confirm",
            "Remove all recommended ranks?",
        );
        const ok = await notifier.confirm(confirmMessage, {
            okTranslateKey: "remove_all",
            okText: this.getTranslatedMessage("remove_all", "Remove All"),
            cancelText: this.getTranslatedMessage("cancel", "Cancel"),
        });
        if (!ok) {
            return;
        }

        this.render.renderLoading();

        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.clearAll();
            const json = (await res.json().catch(() => ({}))) as ClearAllRecommendMutationResponse;
            if (!res.ok || !json?.success) {
                notifier.error(
                    json?.message ||
                        this.getTranslatedMessage("product_recommend_error_remove", "Failed to clear recommends"),
                );
                await this.reloadMeta();
                return;
            }

            const crowns = this.findAllCrownsAll();
            crowns.forEach((el) => {
                this.render.setCrownState(el, false);
                this.render.setCrownRank(el, null);
            });

            this.render.close();
        } catch (e) {
            console.error(e);
            notifier.error(this.getTranslatedMessage("product_recommend_error_remove", "Failed to clear recommends"));
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    private async fetchMeta(productId: number): Promise<{ buttons: RecommendButtonVm[]; canUnset: boolean } | null> {
        const res = await this.api.getMeta(productId);
        const json = (await res.json().catch(() => ({}))) as unknown;

        if (!res.ok || !this.isRecommendMetaResponse(json) || !this.isRecommendMetaSuccessResponse(json)) {
            notifier.error(
                this.getResponseMessage(json) ||
                    this.getTranslatedMessage("product_recommend_error_load", "Failed to load recommend info"),
            );
            return null;
        }

        const meta = json.data;
        const maxRank = Number(meta.max_rank || phpMaxRecommendRank());
        const usedRanks = Array.isArray(meta.used_ranks) ? meta.used_ranks : [];
        const currentRank =
            meta.current_rank === null || typeof meta.current_rank === "undefined" ? null : Number(meta.current_rank);

        const used = new Set(usedRanks.map((n) => Number(n)).filter((n) => Number.isFinite(n)));

        const buttons: RecommendButtonVm[] = [];
        for (let r = 1; r <= maxRank; r++) {
            const isUsed = used.has(r);
            const isCurrent = currentRank !== null && r === currentRank;
            buttons.push({
                rank: r,
                disabled: isUsed && !isCurrent,
                isCurrent,
            });
        }

        return {
            buttons,
            canUnset: currentRank !== null,
        };
    }
}
